// NodeLocationModal.jsx

import React, { useEffect, useRef, useState } from 'react';

// Context
import { useProgress } from 'context/ProgressContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { processLocationSearchResult } from 'utilities/Pins';

// CSS
import './NodeLocationModal.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import TextInput from 'components/textinput/TextInput';
import Pin from 'node/form/fields/location/pin/Pin';

/**
 * NodeLocationModal
 * 
 * Modal for editing a location field value.
 * 
 * @param {object|null} value - Current location field value.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {Function} setOpen - Setter for modal open state.
 * @param {Function} onLocationSaved - Callback when location is saved.
 * 
 * @returns {ReactNode} - NodeLocationModal component.
 */
const NodeLocationModal = ({ value = null, isOpen, setOpen, onLocationSaved }) => {

    // ----- CONTEXT ----- //

    // Global Context

    const { showProgress, hideProgress } = useProgress();

    // User Context
    const { user } = useUser();

    // Theme Context
    const { theme } = useTheme();

    // ----- DEVICE DETECTION ----- //

    // Device Detection

    const isMobile = useIsMobile();

    // ----- REFS ----- //

    // Debounce Ref

    const debounceRef = useRef(null);

    // ----- STATE ----- //

    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [selectedPin, setSelectedPin] = useState(null);
    const [hoveredPlaceId, setHoveredPlaceId] = useState(null);

    // ----- EFFECTS ----- //

    /**
     * Reset when modal opens
     */

    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setResults([]);
            setSelectedPin(value || null);
        }
    }, [isOpen, value]);

    // ----- HANDLERS ----- //

    /**
     * Handle Clear Location
     */

    const handleClearLocation = () => {
        setSelectedPin(null);
        onLocationSaved(null); // Clear the location
        setOpen(false);
    };

    // ----- EFFECTS ----- //

    /**
     * Run autocomplete search with debounce
     */

    useEffect(() => {
        if (!searchTerm || searchTerm.length < 3) {
            setResults([]);
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            getAutocompleteResults(searchTerm);
        }, 400);

        return () => clearTimeout(debounceRef.current);
    }, [searchTerm]);

    // ----- UTILITIES ----- //

    const getAutocompleteResults = async (query) => {
        if (!window.google?.maps?.places) return;

        const autocompleteService = new window.google.maps.places.AutocompleteService();
        autocompleteService.getPlacePredictions({ input: query }, (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                setResults(predictions);
            } else {
                setResults([]);
            }
        });
    };

    // ----- HANDLERS ----- //

    /**
     * Fetch place details and convert to pin
     */

    const handleSelectPrediction = async (prediction) => {
        if (!prediction.place_id) return;

        showProgress('Gathering location details…');

        const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));

        const place = await new Promise((resolve, reject) => {
            placesService.getDetails({ placeId: prediction.place_id }, (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    resolve(place);
                } else {
                    reject(`PlacesServiceStatus not OK: ${status}`);
                }
            });
        });

        try {
            const pin = await processLocationSearchResult(place, user, true);
            pin.result = prediction;

            hideProgress();
            setSelectedPin(pin); // save chosen pin
            setSearchTerm('');   // clear search
            setResults([]);

            // Immediately save and close modal with the pin directly
            onLocationSaved(pin);
            setOpen(false);
        } catch (error) {
            console.error('Error processing location search result:', error);
        }
    };

    // ----- UTILITIES ----- //

    const highlightMatch = (text, query) => {
        if (!query) return text;
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedQuery})`, 'i');
        return text.replace(regex, '<strong>$1</strong>');
    };

    // ----- RENDER ----- //

    return (

        <Modal
          title="Location"
          isOpen={isOpen}
          width={isMobile ? '100%' : 460 }
          height="auto"
          onClose={() => setOpen(false)}
        >
            <div className="node-location-modal-container">

                {/* INPUT WRAPPER */}
                <div className="node-location-modal-input-wrapper">
                    <TextInput
                      value={searchTerm}
                      placeholder="Search for a location..."
                      startIconKey="search"
                      autoFocus
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* RESULTS */}
                <div className="node-location-modal-results">
                    {searchTerm.length > 0 && (
                        <ul
                          className="node-location-modal-dropdown"
                        >
                            {results.map((res) => (
                                <li
                                  className="node-location-modal-dropdown-item"
                                  style={{
                                    backgroundColor: hoveredPlaceId === res.place_id ? theme.backgroundHover : 'transparent',
                                  }}
                                  key={res.place_id}
                                  dangerouslySetInnerHTML={{ __html: highlightMatch(res.description, searchTerm) }}
                                  onClick={() => handleSelectPrediction(res)}
                                  onMouseEnter={() => setHoveredPlaceId(res.place_id)}
                                  onMouseLeave={() => setHoveredPlaceId(null)}
                                />
                            ))}
                        </ul>
                    )}
                </div>

                {/* SELECTED */}
                <div className="node-location-modal-selected">
                    {searchTerm.length === 0 && (
                        <>

                            {/* SELECTED CONTENT */}
                            {(searchTerm.length === 0) && (selectedPin && selectedPin.title) && (

                                <div className="node-location-modal-selected-content">
                                    <Pin
                                      iconKey="check_circle"
                                      pinColor={theme.foregroundMuted}
                                      pinEndColor={theme.foregroundMuted}
                                      pin={selectedPin}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* BUTTONS */}
                <div className="node-location-modal-buttons">
                    {selectedPin && (
                        <Button
                          label="Clear"
                          iconKey="remove"
                          addon="secondary"
                          size="small"
                          onClick={handleClearLocation}
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
};

// Dev Tools
NodeLocationModal.displayName = 'NodeLocationModal';

export default NodeLocationModal;
